
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";

interface KarmaCardProps {
  karmaPoints: number;
  karmaLevel: number;
}

const KarmaCard = ({ karmaPoints, karmaLevel }: KarmaCardProps) => {
  const [progressValue, setProgressValue] = useState(0);
  
  // Calculate karma level progress with animation
  useEffect(() => {
    const progress = calculateKarmaProgress();
    // Animate the progress value
    const timer = setTimeout(() => {
      setProgressValue(progress);
    }, 400);
    return () => clearTimeout(timer);
  }, [karmaPoints, karmaLevel]);

  const calculateKarmaProgress = () => {
    // Level thresholds - matching the ones in karma-utils.ts
    const levelThresholds = [0, 100, 250, 500, 1000, 2000];
    
    // If at max level, show 100%
    if (karmaLevel >= levelThresholds.length - 1) {
      return 100;
    }
    
    const currentThreshold = levelThresholds[karmaLevel];
    const nextThreshold = levelThresholds[karmaLevel + 1];
    const pointsInCurrentLevel = karmaPoints - currentThreshold;
    const pointsNeededForNextLevel = nextThreshold - currentThreshold;
    
    return Math.min(Math.floor((pointsInCurrentLevel / pointsNeededForNextLevel) * 100), 100);
  };
  
  return (
    <motion.div 
      className="bg-primary/5 rounded-lg p-4 text-center"
      whileHover={{ y: -5, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex flex-col items-center">
        <Star className="h-6 w-6 text-yellow-500 mb-1" />
        <span className="text-sm font-medium">Karma Points</span>
        <span className="text-2xl font-bold">{karmaPoints || 0}</span>
        <div className="w-full mt-2">
          <div className="flex justify-between text-xs mb-1">
            <span>Level {karmaLevel || 1}</span>
            <span>{calculateKarmaProgress()}%</span>
          </div>
          <Progress value={progressValue} className="h-2 transition-all duration-1000 ease-out" />
        </div>
      </div>
    </motion.div>
  );
};

export default KarmaCard;
